import { localSandboxEnabled, localSession } from './localSandbox';
import { reportAuthEvent } from './authTelemetry';
/**
 * Token Manager Utility
 * Handles JWT token refresh and expiration checking
 */

import { fetchAuthSession } from '@aws-amplify/auth';
import { STORAGE_KEYS } from '../constants/storage';

const TOKEN_KEY = STORAGE_KEYS.AUTH_TOKEN;
const ACCESS_TOKEN_KEY = STORAGE_KEYS.ACCESS_TOKEN;
const REFRESH_TOKEN_KEY = STORAGE_KEYS.REFRESH_TOKEN;
const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry

/**
 * Decode JWT token without verification (client-side only)
 */
export function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Check if token is expired or will expire soon
 */
export function isTokenExpired(token, bufferMs = TOKEN_EXPIRY_BUFFER) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  
  return currentTime >= (expiryTime - bufferMs);
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpiry(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  return decoded.exp * 1000;
}

/**
 * Get time remaining until token expires (in seconds)
 */
export function getTimeUntilExpiry(token) {
  const expiryTime = getTokenExpiry(token);
  if (!expiryTime) {
    return 0;
  }
  const remaining = Math.floor((expiryTime - Date.now()) / 1000);
  return Math.max(0, remaining);
}

/**
 * Refresh the authentication token via Amplify (uses internally stored refresh token)
 */
export async function refreshAuthToken() {
  try {
    const { tokens } = localSandboxEnabled
      ? await localSession('refresh', { token: localStorage.getItem(TOKEN_KEY) })
      : await fetchAuthSession({ forceRefresh: true });

    if (!tokens?.idToken) {
      throw new Error('No tokens returned from session refresh');
    }

    const idToken = tokens.idToken.toString();
    const accessToken = tokens.accessToken?.toString();

    localStorage.setItem(TOKEN_KEY, idToken);
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }

    reportAuthEvent('token_refresh', 'success', idToken);
    return idToken;
  } catch (error) {
    reportAuthEvent('token_refresh', 'failure');
    console.error('[Token Manager] Error refreshing token:', error);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    throw error;
  }
}

/**
 * Get valid token (refresh if needed)
 */
export async function getValidToken() {
  const token = localStorage.getItem(TOKEN_KEY);

  if (!token) {
    return null;
  }

  // Check if token needs refresh
  if (isTokenExpired(token)) {
    console.log('[Token Manager] Token expired or expiring soon, refreshing...');
    try {
      return await refreshAuthToken();
    } catch (error) {
      console.error('[Token Manager] Failed to refresh token:', error);
      return null;
    }
  }

  return token;
}

/**
 * Get valid access token (for Cognito operations like password change)
 */
export async function getValidAccessToken() {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!accessToken) {
    return null;
  }

  // Check if token needs refresh
  if (isTokenExpired(accessToken)) {
    console.log('[Token Manager] Access token expired or expiring soon, refreshing...');
    try {
      await refreshAuthToken(); // This will update both tokens
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('[Token Manager] Failed to refresh access token:', error);
      return null;
    }
  }

  return accessToken;
}

/**
 * Save tokens after login
 */
export function saveTokens(idToken, refreshToken, accessToken) {
  localStorage.setItem(TOKEN_KEY, idToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }
}

/**
 * Clear all tokens (logout)
 */
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(STORAGE_KEYS.USER); // Also clear user data
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  const token = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  
  if (!token || !refreshToken) {
    return false;
  }

  // Check if token is valid (not expired beyond refresh window)
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return false;
  }

  // Token is valid if it's not expired or we have a refresh token
  return true;
}

/**
 * Schedule automatic token refresh
 * Returns cleanup function
 */
export function scheduleTokenRefresh(onRefreshSuccess, onRefreshFailure) {
  let timeoutId = null;

  const scheduleNext = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    
    if (!token) {
      console.log('[Token Manager] No token to schedule refresh for');
      return;
    }

    const timeUntilExpiry = getTimeUntilExpiry(token);
    
    // Schedule refresh 5 minutes before expiry
    const refreshIn = Math.max(0, (timeUntilExpiry - 300)) * 1000; // Convert to ms

    timeoutId = setTimeout(async () => {
      try {
        const newToken = await refreshAuthToken();
        if (onRefreshSuccess) {
          onRefreshSuccess(newToken);
        }
        // Schedule next refresh
        scheduleNext();
      } catch (error) {
        console.error('[Token Manager] Scheduled refresh failed:', error);
        if (onRefreshFailure) {
          onRefreshFailure(error);
        }
      }
    }, refreshIn);
  };

  // Start scheduling
  scheduleNext();

  // Return cleanup function
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
}

export default {
  decodeToken,
  isTokenExpired,
  getTokenExpiry,
  getTimeUntilExpiry,
  refreshAuthToken,
  getValidToken,
  saveTokens,
  clearTokens,
  isAuthenticated,
  scheduleTokenRefresh,
};
